import { Button, Combobox, Input, Spinner } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CHART_DESCRIPTION_MAX_LENGTH,
  CUSTOM_CHART_TYPES,
  type CustomChartSize,
  type CustomChartType,
} from '@/entities/dashboards';
import { RAW_INTERPOLATION } from '@/shared/lib';
import { AiTextarea } from '@/shared/ui';
import type { ChartAppearanceValues, PresetChartFormValues } from '../../model/dashboardPresetForm';
import type { ChartSizeChange } from '../../model/useDashboardPresetEditor';
import type { CustomChartPanelFilters } from '../DashboardPresetGrid/CustomChartCard';
import { ChartAppearanceFields } from './ChartAppearanceFields';
import { ChartBindingFields, type SourceChartOption } from './ChartBindingFields';
import { CHART_SIZE_LABEL_KEY, CHART_TYPE_LABEL_KEY, isRecommendedSize, sizesForType } from './chartCatalog';
import { CHART_DESCRIPTION_FIELD_CLASS } from './chartDescriptionField';
import { ChartQueryEditor } from './ChartQueryEditor';
import { ChartRuleErrorNotice } from './ChartRuleErrorNotice';
import { ChartTemplateField } from './ChartTemplateField';
import { useChartPropertiesForm } from './useChartPropertiesForm';

type Props = {
  /** Диаграмма, свойства которой правятся */
  chart: PresetChartFormValues;
  /** Фильтры панели страницы: ими диаграмма наполнена, ими же известны значения её выборки */
  filters: CustomChartPanelFilters;
  /** Прочие диаграммы дашборда: любая из них может стать ведущей для этой */
  sourceCharts: SourceChartOption[];
  /** Описание истолковано — диаграмма перерисовывается на своём месте сразу */
  onResolve: (chart: PresetChartFormValues) => void;
  /** Введённое описание остаётся за диаграммой, когда блок сворачивают, не сохранив свойств */
  onKeepDescription: (chartId: string, description: string) => void;
  /** Выбранный размер встаёт на сетке сразу, не дожидаясь сохранения свойств */
  onResize: (change: ChartSizeChange) => void;
  /** Заданное оформление диаграмма принимает сразу — как и размер */
  onRestyle: (appearance: ChartAppearanceValues) => void;
  /** Текст результата диаграмма принимает по уходу из поля — как размер и оформление */
  onRetemplate: (template: string | undefined) => void;
  /** Правки приняты — диаграмма с закреплённым правилом возвращается в состав дашборда */
  onSave: (chart: PresetChartFormValues) => void;
  onCancel: () => void;
};

/**
 * Свойства диаграммы: описание нужных данных, пересказ понятого, название, тип, размер — и далее
 * прочие поля. Порядок идёт от того, что диаграмму задаёт, к тому, как она показана.
 *
 * Поле описания вмещает длинный текст: показывает разом несколько строк, а не поместившееся
 * прокручивает — описание бывает длинным и содержит пожелания к типу и размеру диаграммы. Правка
 * описания истолковывается потерей фокуса, и до тех пор, пока правило не отвечает набранному
 * описанию и выбранному типу, свойства за дашбордом не закрепляются.
 *
 * Размер, в отличие от прочих свойств, применяется к диаграмме сразу: пользователь выбирает долю
 * ширины, глядя на саму диаграмму в её строке, а не вслепую. Отказ от правки свойств возвращает
 * размер, с которым блок разворачивали, — наравне с остальными несохранёнными правками.
 *
 * Текст результата правит всякий, кто правит дашборд: он ничего не отбирает, а лишь показывает
 * уже отобранное. Сам запрос диаграммы показан только при разрешении на его правку.
 */
export const ChartPropertiesForm: FC<Props> = ({
  chart,
  filters,
  sourceCharts,
  onResolve,
  onKeepDescription,
  onResize,
  onRestyle,
  onRetemplate,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation(['common', 'qualityDashboard']);
  const {
    control,
    chartType,
    size,
    description,
    summary,
    columns,
    ruleDraft,
    error,
    isResolving,
    isTemplateShown,
    isRowSectionShown,
    isQueryShown,
    isRuleActual,
    sliceBinding,
    sliceValue,
    isSourceChartShown,
    sourceChartId,
    barOrientation,
    barOrder,
    categoryColors,
    valueColorRules,
    coloredColumns,
    categories,
    colorableColumns,
    handleChangeDescription,
    handleResolveDescription,
    handleChangeType,
    handleChangeSize,
    handleChangeQuery,
    handleChangeTemplate,
    handleCommitTemplate,
    handleChangeSliceValue,
    handleChangeSourceChart,
    handleChangeBarOrientation,
    handleChangeBarOrder,
    handleChangeCategoryColors,
    handleChangeValueColorRules,
    handleChangeColoredColumns,
    handleApply,
    handleCancel,
  } = useChartPropertiesForm({
    chart,
    filters,
    onResolve,
    onKeepDescription,
    onResize,
    onRestyle,
    onRetemplate,
    onSave,
    onCancel,
  });

  const typeOptions = useMemo(
    () => CUSTOM_CHART_TYPES.map((type) => ({ label: t(CHART_TYPE_LABEL_KEY[type]), value: type })),
    [t],
  );

  const sizeOptions = useMemo(
    () =>
      sizesForType(chartType).map((chartSize) => ({
        label: isRecommendedSize(chartType, chartSize)
          ? t('qualityDashboard:presets.recommendedSize', {
              size: t(CHART_SIZE_LABEL_KEY[chartSize]),
              ...RAW_INTERPOLATION,
            })
          : t(CHART_SIZE_LABEL_KEY[chartSize]),
        value: chartSize,
      })),
    [chartType, t],
  );

  const selectType = (value: unknown) => {
    void handleChangeType((value as CustomChartType | undefined) ?? chartType);
  };

  const selectSize = (value: unknown) => {
    handleChangeSize((value as CustomChartSize | undefined) ?? size);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <AiTextarea
          name="description"
          value={description}
          onChange={(event) => handleChangeDescription(event.target.value)}
          onBlur={handleResolveDescription}
          isProcessing={isResolving}
          label={t('qualityDashboard:presets.editor.chartDescription')}
          placeholder={t('qualityDashboard:presets.editor.chartDescriptionPlaceholder')}
          maxLength={CHART_DESCRIPTION_MAX_LENGTH}
          error={description.trim().length === 0 ? t('common:required') : undefined}
          classNames={{ input: CHART_DESCRIPTION_FIELD_CLASS }}
        />

        {summary && (
          <div className="flex flex-col gap-1">
            <p className="text-xs text-muted-foreground">{t('qualityDashboard:presets.editor.ruleSummary')}</p>
            <p className="text-sm">{summary}</p>
          </div>
        )}

        <Input
          control={control}
          name="title"
          label={t('qualityDashboard:presets.editor.chartTitle')}
          rules={{ required: { value: true, message: t('common:required') } }}
        />

        <div className="grid grid-cols-1 gap-4 @md/chart-properties:grid-cols-2">
          <Combobox
            label={t('qualityDashboard:presets.editor.chartType')}
            value={chartType}
            onChange={selectType}
            options={typeOptions}
            isSearchable={false}
            className="w-full"
          />
          <Combobox
            label={t('qualityDashboard:presets.editor.chartSize')}
            value={size}
            onChange={selectSize}
            options={sizeOptions}
            isSearchable={false}
            className="w-full"
          />
        </div>

        <ChartBindingFields
          sliceBinding={sliceBinding}
          sliceValue={sliceValue}
          onChangeSliceValue={handleChangeSliceValue}
          isSourceChartShown={isSourceChartShown}
          sourceCharts={sourceCharts}
          sourceChartId={sourceChartId}
          onChangeSourceChart={handleChangeSourceChart}
        />

        {isTemplateShown && (
          <ChartTemplateField
            value={ruleDraft.template}
            onChange={handleChangeTemplate}
            onCommit={handleCommitTemplate}
            columns={columns}
            isRowSectionShown={isRowSectionShown}
          />
        )}

        {isQueryShown && <ChartQueryEditor query={ruleDraft.query} onChange={handleChangeQuery} />}

        <ChartAppearanceFields
          chartType={chartType}
          barOrientation={barOrientation}
          onChangeBarOrientation={handleChangeBarOrientation}
          barOrder={barOrder}
          onChangeBarOrder={handleChangeBarOrder}
          categories={categories}
          categoryColors={categoryColors}
          onChangeCategoryColors={handleChangeCategoryColors}
          valueColorRules={valueColorRules}
          onChangeValueColorRules={handleChangeValueColorRules}
          colorableColumns={colorableColumns}
          coloredColumns={coloredColumns}
          onChangeColoredColumns={handleChangeColoredColumns}
        />

        {error && <ChartRuleErrorNotice error={error} onSelectType={handleChangeType} />}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {isResolving && (
          <span className="mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="size-4" />
            {t('qualityDashboard:presets.editor.resolving')}
          </span>
        )}
        <Button variant="ghost" onClick={handleCancel}>
          {t('common:cancel')}
        </Button>
        <Button disabled={isResolving || !isRuleActual} onClick={handleApply}>
          {t('common:save')}
        </Button>
      </div>
    </div>
  );
};
